import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { getFileContent, postFileUpload } from "tm-shared/api";

import { APP_VERSION_CONFIG, APP_VERSION_PERSIST } from "@/config";
import { RootState } from "@/store";
import {
    RestoreConfigFromServer,
    restoreConfigFromServer,
    RestoreProgressFromServer,
    restoreProgressFromServer,
} from "@/store/actions";
import { setProgressLastSyncedAt } from "@/store/slices/offline";

export const saveProgressToServerThunk = createAsyncThunk<
    void,
    void,
    { state: RootState }
>("state/saveProgressToServer", async (_, { getState, dispatch }) => {
    const state = getState();
    const {
        user,
        progress,
        offline: { requestQueue },
    } = state;

    if (!user?.id || !user?.role) {
        throw new Error("User not authenticated");
    }

    const isoDate = dayjs().toISOString();

    const payload = {
        state: { progress, requestQueue },
        createdAt: isoDate,
        version: APP_VERSION_PERSIST,
    };

    const jsonString = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(jsonString);

    let binary = "";
    for (const b of bytes) binary += String.fromCharCode(b);

    const base64Content = btoa(binary);

    try {
        await postFileUpload({
            base64Content,
            filename: `${user.role}.json`,
            space: user.id,
            middlePath: "front_states",
        });

        dispatch(setProgressLastSyncedAt(isoDate));
    } catch (error) {
        console.error(error);
    }
});

interface ProgressFromServer {
    state: RestoreProgressFromServer;
    createdAt: string;
    version: number;
}

export const loadProgressFromServer = createAsyncThunk<
    void,
    { userId: string; roleSlug: string },
    { state: RootState }
>(
    "state/loadProgressFromServer",
    async ({ userId, roleSlug }, { dispatch, rejectWithValue, getState }) => {
        try {
            const { result } = await getFileContent({
                filePath: `${userId}/front_states/${roleSlug}.json`,
            });

            const { lastModifiedAt } = getState().offline.progress;

            const bytes = Uint8Array.from(atob(result), (c) => c.charCodeAt(0));
            const jsonString = new TextDecoder("utf-8").decode(bytes);
            const data: ProgressFromServer = JSON.parse(jsonString);

            if (!data?.createdAt || !data?.version || !data?.state) return;

            const now = dayjs();

            const createdAt = dayjs(data.createdAt);
            const serverIsToday = createdAt.isSame(now, "day");

            const last = lastModifiedAt ? dayjs(lastModifiedAt) : null;
            const hasValidLast = !!last && last.isValid();

            const serverIsNewerThanLocal = !hasValidLast
                ? true
                : createdAt.isAfter(last);

            const isValidVersion = APP_VERSION_PERSIST === data.version;

            if (serverIsToday && isValidVersion && serverIsNewerThanLocal) {
                dispatch(restoreProgressFromServer(data.state));
            }
        } catch (error) {
            console.error("Error loading state from server:", error);
            return rejectWithValue(error);
        }
    },
);

export const saveConfigToServerThunk = createAsyncThunk<
    void,
    void,
    { state: RootState }
>("state/saveConfigToServerThunk", async (_, { getState }) => {
    const state = getState();
    const { user, config } = state;

    if (!user?.id || !user?.role) {
        throw new Error("User not authenticated");
    }

    const payload = {
        config,
        version: APP_VERSION_CONFIG,
    };

    const jsonString = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(jsonString);

    let binary = "";
    for (const b of bytes) binary += String.fromCharCode(b);

    const base64Content = btoa(binary);

    try {
        await postFileUpload({
            base64Content,
            filename: `${user.role}.json`,
            space: user.id,
            middlePath: "front_config",
        });
    } catch (error) {
        console.error(error);
    }
});

interface ConfigFromServer {
    config: RestoreConfigFromServer;
    version: number;
}

export const loadConfigFromServer = createAsyncThunk<
    void,
    { userId: string; roleSlug: string },
    { state: RootState }
>(
    "state/loadConfigFromServer",
    async ({ userId, roleSlug }, { dispatch, rejectWithValue }) => {
        try {
            const { result } = await getFileContent({
                filePath: `${userId}/front_config/${roleSlug}.json`,
            });

            const bytes = Uint8Array.from(atob(result), (c) => c.charCodeAt(0));
            const jsonString = new TextDecoder("utf-8").decode(bytes);
            const data: ConfigFromServer = JSON.parse(jsonString);

            if (!data?.version || !data?.config) return;

            const isValidVersion = APP_VERSION_CONFIG === data.version;

            if (isValidVersion) {
                dispatch(restoreConfigFromServer(data.config));
            }
        } catch (error) {
            console.error("Error loading state from server:", error);
            return rejectWithValue(error);
        }
    },
);
